import { Action } from "@packages/shared-types";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { AlertTriangle, Plus, Receipt } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { listPayablesQueryOptions } from "@/api/financial";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { Button } from "@/components/ui/button";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { useHasPermission } from "@/hooks/useAccessControl";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { useFarmScope } from "@/hooks/useFarmScope";
import { formatMoney } from "@/utils/financial";
import {
	FinancialHeaderActions,
	HeaderActionButton,
} from "../-components/FinancialHeaderActions";
import { StatCard } from "../-components/StatCard";
import { PayableStatusBadge } from "./-components/PayableBadges";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/financial/payables/",
)({
	component: PayablesListPage,
});

function PayablesListPage() {
	const { orgId } = Route.useParams();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const scope = useFarmScope();
	const { formatDateOnly } = useDateFormatter();
	const canManage = useHasPermission(orgId)(Action.MANAGE_FINANCE);

	const establishmentId =
		scope.kind === "single" ? scope.establishmentId : undefined;

	const queryParams = useMemo(
		() => ({
			...(establishmentId ? { establishmentId } : {}),
		}),
		[establishmentId],
	);

	const { data, isLoading } = useQuery(
		listPayablesQueryOptions(orgId, queryParams),
	);

	const currency = data?.currency ?? "BRL";
	const summary = data?.summary;

	// The backend returns the authoritative ordering (overdue-first, then next
	// due date, settled, void last) — render as-is, no client re-sort.
	const rows = data?.data ?? [];

	return (
		<div className="flex flex-col gap-4">
			{canManage && (
				<FinancialHeaderActions>
					<HeaderActionButton
						icon={Plus}
						label={t("financial.payables.form.newAction")}
						shortLabel={t("financial.payables.form.newActionShort")}
						onClick={() =>
							navigate({
								to: "/app/org/$orgId/financial/payables/new",
								params: { orgId },
							})
						}
					/>
				</FinancialHeaderActions>
			)}

			<div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
				<StatCard
					label={t("financial.payables.summary.totalOutstanding")}
					value={formatMoney(summary?.totalOutstanding ?? "0", currency)}
					icon={Receipt}
				/>
				<StatCard
					label={t("financial.payables.summary.overdueCount")}
					value={String(summary?.overdueCount ?? 0)}
					icon={AlertTriangle}
					tone={summary && summary.overdueCount > 0 ? "negative" : "neutral"}
				/>
				<StatCard
					label={t("financial.payables.summary.overdueAmount")}
					value={formatMoney(summary?.overdueAmount ?? "0", currency)}
					icon={AlertTriangle}
					tone={
						summary && Number.parseFloat(summary.overdueAmount) > 0
							? "negative"
							: "neutral"
					}
				/>
			</div>

			<div className="card bg-base-100 shadow-md">
				<div className="card-body">
					{isLoading ? (
						<div className="flex justify-center py-16">
							<LoadingSpinner />
						</div>
					) : rows.length === 0 ? (
						<div className="py-12 text-center">
							<p className="font-medium">
								{t("financial.payables.empty.title")}
							</p>
							<p className="mt-1 text-sm text-base-content/60">
								{t("financial.payables.empty.description")}
							</p>
							{canManage && (
								<Link
									to="/app/org/$orgId/financial/payables/new"
									params={{ orgId }}
									className="mt-4 inline-block"
								>
									<Button variant="outline">
										<Plus className="mr-2 h-4 w-4" />
										{t("financial.payables.form.newAction")}
									</Button>
								</Link>
							)}
						</div>
					) : (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>
										{t("financial.payables.columns.vendor")}
									</TableHead>
									<TableHead className="hidden sm:table-cell">
										{t("financial.payables.columns.issueDate")}
									</TableHead>
									<TableHead>
										{t("financial.payables.columns.nextDueDate")}
									</TableHead>
									<TableHead className="text-right hidden md:table-cell">
										{t("financial.payables.columns.total")}
									</TableHead>
									<TableHead className="text-right hidden md:table-cell">
										{t("financial.payables.columns.paid")}
									</TableHead>
									<TableHead className="text-right">
										{t("financial.payables.columns.outstanding")}
									</TableHead>
									<TableHead>
										{t("financial.payables.columns.status")}
									</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{rows.map((bill) => (
									<TableRow
										key={bill.id}
										className="cursor-pointer"
										onClick={() =>
											navigate({
												to: "/app/org/$orgId/financial/payables/$billId",
												params: { orgId, billId: bill.id },
											})
										}
									>
										<TableCell>
											<span className="font-medium">{bill.party}</span>
											{bill.description && (
												<span className="block text-xs text-base-content/60">
													{bill.description}
												</span>
											)}
										</TableCell>
										<TableCell className="hidden sm:table-cell">
											{formatDateOnly(new Date(bill.issueDate).getTime())}
										</TableCell>
										<TableCell
											className={bill.isOverdue ? "text-error" : undefined}
										>
											{bill.nextDueDate
												? formatDateOnly(new Date(bill.nextDueDate).getTime())
												: "—"}
										</TableCell>
										<TableCell className="text-right hidden md:table-cell">
											{formatMoney(bill.totalAmount, currency)}
										</TableCell>
										<TableCell className="text-right hidden md:table-cell text-success">
											{formatMoney(bill.paidAmount, currency)}
										</TableCell>
										<TableCell className="text-right font-medium">
											{formatMoney(bill.outstandingAmount, currency)}
										</TableCell>
										<TableCell>
											<PayableStatusBadge status={bill.displayStatus} />
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</div>
			</div>
		</div>
	);
}
